import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import HorizontalTitleList from '@/components/timeline/HorizontalTitleList.vue';

import { ImpressionTrackingEvents } from '@/enums/events';
import { TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import type { PosterGraphqlProps } from '@/interfaces/components/poster/PosterGraphql';
import type { HorizontalTitleListItem } from '@/components/timeline/types';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { ListTitleSR } from '@/features/sponsored-recommendations/types';

import { SraEventAction } from '@/@types/graphql-types';
import { useActivateExperiment } from '@/libraries/experiment/composables/useExperiment';
import { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';
import { useXmasJwtvTvodSRExperiment } from '@/components/experiments/XmasJwtvTvodSR';


export default /*#__PURE__*/_defineComponent({
  __name: 'SRTitleListGraphql',
  props: {
    fullWidth: { type: Boolean },
    titles: null,
    sponsoredAd: null,
    additionalContexts: null,
    impressionTrackingLabel: null,
    impressionTrackingProperty: null,
    impressionTrackingCategory: null,
    showSponsoredAd: { type: Boolean },
    placement: null
  },
  emits: ["onPosterClick", "onPosterClickOut", "imageError"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'onPosterClick', data: { index: number; title: any; titleList?: string }): void;
	(e: 'onPosterClickOut', data: { index: number; title: any; titleList?: string; event: SraEventAction }): void;
	(e: 'imageError', src: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	fullWidth: boolean;
	titles: ListTitleSR[];
	sponsoredAd: SponsoredAdFragment | undefined;
	additionalContexts?: SnowplowContext[];
	impressionTrackingLabel?: string;
	impressionTrackingProperty?: string;
	impressionTrackingCategory?: any;
	showSponsoredAd: boolean;
	placement?: ClickoutPlacement;
};





// Note(valerio.mazza): SSR special case we need to track the Event on created
if (process.server && props.titles?.length) {
	trackSimilarTitlesLoaded();
}

const posterOverrides = computed(() => {
	if (!props.sponsoredAd?.campaign) return null;

	const overrides = props.sponsoredAd?.campaign?.nodeOverrides ?? [];
	return Object.fromEntries(overrides.map(({ nodeId: id, promotionalImageUrl: url }) => [id, url]));
});

function transformPosterProps(posterProps: PosterGraphqlProps, title: HorizontalTitleListItem): PosterGraphqlProps {
	const posterUrl = posterOverrides.value?.[title.id];
	if (posterUrl == null) return posterProps;

	return {
		...posterProps,
		sponsoredRecImages: {
			imageUrl: posterUrl,
			sourceSets: [
				{
					srcSet: posterUrl?.replace(/\.\w+$/, '.avif'),
					imageProp: {
						extension: '.avif',
						type: 'image/avif',
					},
					sourceProp: {
						viewport: 'desktop',
					},
					media: null,
				},
			],
		},
		title: {
			...title,
			content: {
				...title.content,
				posterUrl,
			},
		} as HorizontalTitleListItem,
	};
}

const transformedTitles = computed(() => {
	if (!props.sponsoredAd?.campaign) return props.titles;

	const overrides = props.sponsoredAd?.campaign?.nodeOverrides ?? [];

	const mapTitleIdWithUrl = Object.fromEntries(
		overrides.map(({ nodeId: id, watchNowOffer }) => [id, watchNowOffer?.standardWebURL])
	);

	// If we have a overwriten watchNowOffer unique to the title, we want to
	// inject that into the title object, replacing content.watchNowOffer
	return props.titles.map(title => {
		if (mapTitleIdWithUrl[title.id]) {
			title.content.fullPath = mapTitleIdWithUrl[title.id] || title.content.fullPath;
		}

		return title;
	});
});

function trackSimilarTitlesLoaded() {
	const properties: TrackingProviderPropertiesInterface = { action: 'loaded', nonInteraction: true };
	if (props.impressionTrackingLabel) properties.label = props.impressionTrackingLabel;

	TrackingHelper.trackEvent(ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS, properties, props.additionalContexts);
}

function trackScroll(label: string) {
	const properties: TrackingProviderPropertiesInterface = { action: 'horizontal_scroller_clicked', label };
	if (props.impressionTrackingLabel) properties.property = props.impressionTrackingLabel;

	TrackingHelper.trackEvent('userinteraction', properties, props.additionalContexts);
}
// EXP_JWTV_SR_XMAS_TVOD_TRACKING
const isJWTVXmasSRA = computed(() => {
	return props.sponsoredAd?.campaign?.name === 'JWTV_XMAS_AU_BURST1_SRA';
});

useActivateExperiment('EXP_JWTV_SR_XMAS_TVOD_TRACKING', isJWTVXmasSRA);
const { onPosterXmasSRClick } = useXmasJwtvTvodSRExperiment();
async function onPosterClick(event: MouseEvent, title: HorizontalTitleListItem, index: number) {
	if (isJWTVXmasSRA.value) {
		event.stopPropagation();
		event.preventDefault();

		emit('onPosterClick', {
			titleList: props.sponsoredAd?.campaign?.node.nodeId,
			title,
			index,
		});
		emit('onPosterClickOut', {
			titleList: props.sponsoredAd?.campaign?.node.nodeId,
			event: SraEventAction.Clickout,
			title,
			index,
		});

		onPosterXmasSRClick(title, props.placement);
	}
}
// EXP_JWTV_SR_XMAS_TVOD_TRACKING

return { __sfc: true,props, emit, posterOverrides, transformPosterProps, transformedTitles, trackSimilarTitlesLoaded, trackScroll, isJWTVXmasSRA, onPosterXmasSRClick, onPosterClick, PosterGraphql, HorizontalTitleList }
}

})